import React, { useState, useEffect } from 'react';
import { api } from '../api';

const MODEL_PRICES = {
  'gpt-5.6-sol': { input: 5.00, output: 30.00 },
  'gpt-5.6-terra': { input: 2.50, output: 15.00 },
  'gpt-5.6-luna': { input: 1.00, output: 6.00 },
  'gpt-6-astra': { input: 10.00, output: 50.00 },
  'claude-sonnet-4-20250514': { input: 3.00, output: 15.00 },
  'claude-haiku-4-5-20251001': { input: 0.80, output: 4.00 },
};

function calcCost(model, inp, out) {
  const p = MODEL_PRICES[model] || { input: 2.50, output: 10.00 };
  return (inp || 0) * p.input / 1000000 + (out || 0) * p.output / 1000000;
}

// Файлы и картинки на сервере отдаются только с токеном
function withToken(url) {
  if (!url || !url.startsWith('/api/')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${localStorage.getItem('token')}`;
}

function providerLabel(provider) {
  if (provider === 'openai') return 'GPT';
  if (provider === 'anthropic') return 'Claude';
  if (provider === 'image') return 'Картинки';
  if (provider === 'video') return 'Видео';
  return provider;
}

function providerBadgeClass(provider) {
  return provider === 'anthropic' ? 'admin' : 'manager';
}

function formatDuration(sec) {
  if (!sec || sec <= 0) return '0 мин';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}ч ${m}м`;
  return `${m} мин`;
}

export default function MonitoringPage({ user: currentUser }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [sessions, setSessions] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionMsgs, setSessionMsgs] = useState([]);
  const [userFiles, setUserFiles] = useState({ files: [], skills_text: '', role_description: '' });
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [userImages, setUserImages] = useState([]);
  const [userVideos, setUserVideos] = useState([]);
  const [previewImg, setPreviewImg] = useState(null);

  useEffect(() => {
    api.getUsers().then(setUsers).catch(() => {});
  }, []);

  async function selectUser(userId) {
    setSelectedUser(userId);
    setExpandedSession(null);
    setSessionMsgs([]);
    setTab('dashboard');
    if (!userId) return;

    try {
      const [sess, files, analyticsData, imgs, vids] = await Promise.all([
        api.adminSessions(userId),
        api.getUserFiles(userId),
        api.statsAnalytics(),
        api.adminImages(userId),
        api.adminVideos(userId),
      ]);
      setSessions(sess);
      setUserFiles(files);
      setUserImages(imgs || []);
      setUserVideos(vids || []);
      const userData = analyticsData.users?.find(u => u.id === userId);
      setAnalytics(userData || null);
    } catch {}
  }

  async function expandSession(s) {
    if (expandedSession === s.session_id) { setExpandedSession(null); return; }
    try {
      const msgs = await api.adminSession(s.session_id);
      setSessionMsgs(msgs);
      setExpandedSession(s.session_id);
    } catch {}
  }

  const user = users.find(u => u.id === selectedUser);

  return (
    <div className="admin-page">
      <h2>Мониторинг сотрудников</h2>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <label style={{ color: 'var(--text2)', fontSize: '0.9rem' }}>Сотрудник:</label>
        <select value={selectedUser} onChange={e => selectUser(e.target.value)}
          style={{ padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: '0.9rem', minWidth: 250 }}>
          <option value="">— Выберите сотрудника —</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.display_login} ({u.internal_name})</option>
          ))}
        </select>
      </div>

      {selectedUser && user && (
        <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {['dashboard', 'chats', 'images', 'videos', 'training'].map(t => (
              <button key={t} className={`provider-btn ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)} style={{ padding: '8px 16px' }}>
                {t === 'dashboard' ? 'Дашборд' : t === 'chats' ? 'Чаты' : t === 'images' ? 'Картинки' : t === 'videos' ? 'Видео' : 'Обучение ИИ'}
              </button>
            ))}
          </div>

          {tab === 'dashboard' && analytics && (
            <div>
              <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <h3 style={{ marginBottom: 8 }}>{user.display_login}</h3>
                <div style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>
                  Системное имя: {user.internal_name} | Роль: {user.role_description || 'Не указана'} | Последний вход: {user.last_login?.slice(0, 16).replace('T', ' ') || 'Нет'}
                </div>
              </div>

              <div className="stats-grid" style={{ marginBottom: 20 }}>
                <div className="stat-card"><div className="label">Сообщений</div><div className="value">{analytics.total_messages}</div></div>
                <div className="stat-card"><div className="label">Токены (вход)</div><div className="value">{(analytics.total_input_tokens || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Токены (выход)</div><div className="value">{(analytics.total_output_tokens || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Картинки</div><div className="value">{analytics.image_count}</div></div>
                <div className="stat-card"><div className="label">Видео</div><div className="value">{analytics.video_count || 0}</div></div>
                <div className="stat-card"><div className="label">Стоимость</div><div className="value">${analytics.total_cost_usd?.toFixed(4)}</div></div>
                <div className="stat-card"><div className="label">Стоимость ₽</div><div className="value">{analytics.total_cost_rub?.toFixed(2)} ₽</div></div>
                <div className="stat-card"><div className="label">Время в чатах</div><div className="value">{formatDuration(analytics.total_session_sec)}</div></div>
              </div>

              {/* Session time by day */}
              {analytics.daily_session_time && analytics.daily_session_time.length > 0 && (
                <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Время сессий по дням</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {analytics.daily_session_time.slice(-14).map((d, i) => (
                      <div key={i} style={{ padding: '6px 12px', background: 'var(--bg3)', borderRadius: 6, fontSize: '0.8rem' }}>
                        <div style={{ color: 'var(--text2)' }}>{d.day}</div>
                        <div style={{ fontWeight: 700 }}>{formatDuration(d.duration_sec)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RFM */}
              <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>RFM Анализ</h4>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div>R (Давность): <strong style={{ color: 'var(--accent2)' }}>{analytics.rfm?.r}/5</strong> <span style={{ color: 'var(--text2)', fontSize: '0.8rem' }}>({analytics.rfm?.days_since_last}д назад)</span></div>
                  <div>F (Частота): <strong style={{ color: 'var(--accent2)' }}>{analytics.rfm?.f}/5</strong> <span style={{ color: 'var(--text2)', fontSize: '0.8rem' }}>({analytics.rfm?.active_days} акт.дней)</span></div>
                  <div>M (Объём): <strong style={{ color: 'var(--accent2)' }}>{analytics.rfm?.m}/5</strong></div>
                  <div>Сегмент: <strong style={{ color: 'var(--accent)' }}>{analytics.rfm?.segment}</strong></div>
                </div>
              </div>

              {/* Images summary */}
              {analytics.image_count > 0 && (
                <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Генерация изображений</h4>
                  <div style={{ display: 'flex', gap: 20, fontSize: '0.9rem' }}>
                    <div>Всего: <strong>{analytics.image_count}</strong></div>
                    <div>Стоимость: <strong style={{ color: 'var(--accent2)' }}>${analytics.image_cost_usd}</strong></div>
                    <div><strong>{(analytics.image_cost_usd * 90).toFixed(2)} ₽</strong></div>
                    {analytics.images_by_model && analytics.images_by_model.map((im, i) => (
                      <div key={i}>Модель {im.model}: <strong>{im.cnt}</strong></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Videos summary */}
              {analytics.video_count > 0 && (
                <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Генерация видео (Sora)</h4>
                  <div style={{ display: 'flex', gap: 20, fontSize: '0.9rem', flexWrap: 'wrap' }}>
                    <div>Всего: <strong>{analytics.video_count}</strong></div>
                    <div>Секунд: <strong>{analytics.video_seconds || 0}</strong></div>
                    <div>Стоимость: <strong style={{ color: 'var(--accent2)' }}>${analytics.video_cost_usd}</strong></div>
                    <div><strong>{((analytics.video_cost_usd || 0) * 90).toFixed(2)} ₽</strong></div>
                    {analytics.videos_by_model && analytics.videos_by_model.map((v, i) => (
                      <div key={i}>{v.model} {v.size}: <strong>{v.cnt}</strong></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Model breakdown */}
              {analytics.breakdown?.length > 0 && (
                <div style={{ padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>По моделям</h4>
                  <table className="users-table">
                    <thead><tr><th>Провайдер</th><th>Модель</th><th>Сообщений</th><th>Токены</th><th>$ USD</th><th>₽ RUB</th></tr></thead>
                    <tbody>
                      {analytics.breakdown.map((b, i) => (
                        <tr key={i}>
                          <td><span className={`badge ${providerBadgeClass(b.provider)}`}>{providerLabel(b.provider)}</span></td>
                          <td>{b.model}</td>
                          <td>{b.messages}{b.is_image ? ' ген.' : b.is_video ? ` ген. / ${b.seconds || 0} сек` : ''}</td>
                          <td>{b.is_image || b.is_video ? '—' : ((b.input_tokens || 0) + (b.output_tokens || 0)).toLocaleString()}</td>
                          <td>${(b.cost_usd || 0).toFixed(4)}</td>
                          <td>{((b.cost_usd || 0) * 90).toFixed(2)} ₽</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'chats' && (
            <div>
              {sessions.length === 0 && <div style={{ color: 'var(--text2)' }}>Нет чатов</div>}
              {sessions.map(s => (
                <div key={s.session_id}>
                  <div className="session-card" onClick={() => expandSession(s)}>
                    <div className="session-meta">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span className={`badge ${providerBadgeClass(s.provider)}`}>{providerLabel(s.provider)}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>{s.model}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: 'var(--text2)' }}>
                        <span>{s.message_count} сообщ.</span>
                        <span>{((s.total_input || 0) + (s.total_output || 0)).toLocaleString()} tok</span>
                        <span style={{ color: 'var(--accent2)' }}>${calcCost(s.model, s.total_input, s.total_output).toFixed(4)}</span>
                        <span>
                          {s.started_at?.slice(0, 16).replace('T', ' ')}
                          {s.last_at && s.last_at.slice(0, 10) !== s.started_at?.slice(0, 10) &&
                            ` → ${s.last_at.slice(0, 16).replace('T', ' ')}`}
                        </span>
                      </div>
                    </div>
                    <div className="session-preview">{s.first_message || '—'}</div>
                  </div>
                  {expandedSession === s.session_id && (
                    <div className="session-messages">
                      {sessionMsgs.map((m, i) => (
                        <div key={i} className={`session-msg ${m.role}`}>
                          <div className="session-msg-label">{m.role === 'user' ? 'Сотрудник' : 'AI'} — {m.created_at?.slice(11, 16)}</div>
                          {m.attachment && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: 'rgba(255,255,255,0.08)', borderRadius: 4, marginBottom: 4, fontSize: '0.8rem' }}>
                              {m.attachment.isImage && <img src={withToken(m.attachment.url)} alt="" style={{ maxWidth: 80, maxHeight: 60, borderRadius: 4 }} />}
                              <span style={{ fontWeight: 600 }}>{m.attachment.originalName}</span>
                            </div>
                          )}
                          {m.content}
                          {m.files?.map((f, fi) => (
                            <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: 'rgba(255,255,255,0.08)', borderRadius: 4, marginTop: 4, fontSize: '0.8rem' }}>
                              <span>📎</span>
                              <a href={withToken(f.url)} download={f.originalName} style={{ fontWeight: 600, color: 'inherit' }}>{f.originalName}</a>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'images' && (
            <div>
              {analytics && analytics.image_count > 0 ? (
                <div>
                  <div className="stats-grid" style={{ marginBottom: 20 }}>
                    <div className="stat-card"><div className="label">Всего картинок</div><div className="value">{analytics.image_count}</div></div>
                    <div className="stat-card"><div className="label">Стоимость $</div><div className="value">${analytics.image_cost_usd}</div></div>
                    <div className="stat-card"><div className="label">Стоимость ₽</div><div className="value">{(analytics.image_cost_usd * 90).toFixed(2)} ₽</div></div>
                  </div>
                  {userImages.length > 0 && (
                    <div style={{ padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
                      <h4 style={{ marginBottom: 12, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Сгенерированные изображения</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                        {userImages.map(img => (
                          <div key={img.id} style={{ background: 'var(--bg3)', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => setPreviewImg(img)}>
                            <img src={withToken(img.image_url)} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                            <div style={{ padding: 8 }}>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text2)', marginBottom: 4 }}>{img.created_at?.slice(0, 16).replace('T', ' ')}</div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.prompt}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text2)', marginTop: 4 }}>{img.model} • {img.size}{img.cost_usd != null ? ` • $${img.cost_usd.toFixed(3)}` : ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {analytics.images_by_model && analytics.images_by_model.length > 0 && (
                    <div style={{ padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                      <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>По моделям</h4>
                      <table className="users-table">
                        <thead><tr><th>Модель</th><th>Количество</th><th>~Стоимость $</th><th>~Стоимость ₽</th></tr></thead>
                        <tbody>
                          {analytics.images_by_model.map((im, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 600 }}>{im.model || 'gpt-image-1'}</td>
                              <td>{im.cnt}</td>
                              <td>${(im.cost_usd || 0).toFixed(2)}</td>
                              <td>{(im.cnt * 0.04 * 90).toFixed(2)} ₽</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: 'var(--text2)' }}>Нет сгенерированных картинок</div>
              )}
              {previewImg && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  onClick={() => setPreviewImg(null)}>
                  <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <img src={withToken(previewImg.image_url)} alt="" style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8 }} />
                    <div style={{ color: '#fff', padding: '12px 0', fontSize: '0.9rem' }}>
                      <div style={{ marginBottom: 4 }}>{previewImg.prompt}</div>
                      <div style={{ fontSize: '0.75rem', color: '#aaa' }}>{previewImg.model} • {previewImg.size}{previewImg.cost_usd != null ? ` • $${previewImg.cost_usd.toFixed(3)} (${previewImg.input_tokens || 0}→${previewImg.output_tokens || 0} ток.)` : ''} • {previewImg.created_at?.slice(0, 16).replace('T', ' ')}</div>
                    </div>
                    <button onClick={() => setPreviewImg(null)} style={{ position: 'absolute', top: -12, right: -12, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 18, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'videos' && (
            <div>
              {analytics && analytics.video_count > 0 ? (
                <div>
                  <div className="stats-grid" style={{ marginBottom: 20 }}>
                    <div className="stat-card"><div className="label">Всего видео</div><div className="value">{analytics.video_count}</div></div>
                    <div className="stat-card"><div className="label">Секунд</div><div className="value">{analytics.video_seconds || 0}</div></div>
                    <div className="stat-card"><div className="label">Стоимость $</div><div className="value">${analytics.video_cost_usd}</div></div>
                    <div className="stat-card"><div className="label">Стоимость ₽</div><div className="value">{((analytics.video_cost_usd || 0) * 90).toFixed(2)} ₽</div></div>
                  </div>
                  {userVideos.filter(v => v.video_url).length > 0 && (
                    <div style={{ padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
                      <h4 style={{ marginBottom: 12, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Сгенерированные видео</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                        {userVideos.filter(v => v.video_url).map(v => (
                          <div key={v.id} style={{ background: 'var(--bg3)', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                            <video src={withToken(v.video_url)} poster={v.thumb_url ? withToken(v.thumb_url) : undefined}
                              controls preload="metadata" style={{ width: '100%', display: 'block', background: '#000' }} />
                            <div style={{ padding: 8 }}>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text2)', marginBottom: 4 }}>{v.created_at?.slice(0, 16).replace('T', ' ')}</div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.prompt}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text2)', marginTop: 4 }}>{v.model} • {v.size} • {v.seconds} сек • ${(v.cost_usd || 0).toFixed(2)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {analytics.videos_by_model && analytics.videos_by_model.length > 0 && (
                    <div style={{ padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                      <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>По моделям</h4>
                      <table className="users-table">
                        <thead><tr><th>Модель</th><th>Разрешение</th><th>Количество</th><th>Секунд</th><th>Стоимость $</th><th>Стоимость ₽</th></tr></thead>
                        <tbody>
                          {analytics.videos_by_model.map((v, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 600 }}>{v.model}</td>
                              <td>{v.size}</td>
                              <td>{v.cnt}</td>
                              <td>{v.secs || 0}</td>
                              <td>${(v.cost || 0).toFixed(2)}</td>
                              <td>{((v.cost || 0) * 90).toFixed(2)} ₽</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: 'var(--text2)' }}>Нет сгенерированных видео</div>
              )}
            </div>
          )}

          {tab === 'training' && (
            <div>
              <div style={{ marginBottom: 20, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Роль в организации</h4>
                <div style={{ color: 'var(--text)', fontSize: '0.9rem' }}>{userFiles.role_description || 'Не указана'}</div>
              </div>
              <div style={{ marginBottom: 20, padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Навыки и контекст</h4>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', color: 'var(--text)', background: 'var(--bg3)', padding: 12, borderRadius: 6 }}>
                  {userFiles.skills_text || 'Не заполнено'}
                </pre>
              </div>
              <div style={{ padding: 16, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ marginBottom: 8, color: 'var(--text2)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Загруженные файлы ({userFiles.files?.length || 0})</h4>
                {(!userFiles.files || userFiles.files.length === 0) ? (
                  <div style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>Нет файлов</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {userFiles.files.map(f => (
                      <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                        <span>{f.original_name}</span>
                        <span style={{ color: 'var(--text2)' }}>{f.created_at?.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
